import { ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

export type UserGender = "male" | "female" | null | undefined;

type SizePreset = "xs" | "sm" | "md" | "lg" | "xl";

interface UserAvatarProps {
  profileImage?: string | null;
  gender?: UserGender;
  alt?: string;
  size?: SizePreset;
  sizeClassName?: string;
  verified?: boolean;
  className?: string;
  circleClassName?: string;
  imageClassName?: string;
}

const SIZE_CLASSES: Record<SizePreset, string> = {
  xs: "h-6 w-6",
  sm: "h-8 w-8",
  md: "h-10 w-10",
  lg: "h-16 w-16",
  xl: "h-24 w-24",
};

const BADGE_CLASSES: Record<SizePreset, { wrapper: string; icon: string }> = {
  xs: { wrapper: "h-3 w-3 -bottom-0.5 -right-0.5", icon: "h-2 w-2" },
  sm: { wrapper: "h-4 w-4 -bottom-0.5 -right-0.5", icon: "h-2.5 w-2.5" },
  md: { wrapper: "h-[18px] w-[18px] -bottom-0.5 -right-0.5", icon: "h-3 w-3" },
  lg: { wrapper: "h-5 w-5 -bottom-1 -right-1", icon: "h-3.5 w-3.5" },
  xl: { wrapper: "h-6 w-6 -bottom-1 -right-1", icon: "h-4 w-4" },
};

const DEFAULT_MALE_AVATAR = "/imgs/male_default_avatar.png";
const DEFAULT_FEMALE_AVATAR = "/imgs/female_default_avatar.png";

export const defaultAvatarFor = (gender: UserGender): string =>
  gender === "female" ? DEFAULT_FEMALE_AVATAR : DEFAULT_MALE_AVATAR;

export const UserAvatar = ({
  profileImage,
  gender,
  alt = "",
  size = "md",
  sizeClassName,
  verified = false,
  className,
  circleClassName,
  imageClassName,
}: UserAvatarProps) => {
  const src = profileImage || defaultAvatarFor(gender);
  const badge = BADGE_CLASSES[size];

  return (
    <div className={cn("relative flex-shrink-0", className)}>
      <div
        className={cn(
          "rounded-full bg-muted overflow-hidden flex items-center justify-center",
          sizeClassName ?? SIZE_CLASSES[size],
          circleClassName
        )}
      >
        <img src={src} alt={alt} className={cn("h-full w-full object-cover", imageClassName)} />
      </div>
      {verified && (
        <div
          className={cn(
            "absolute rounded-full bg-green-500 flex items-center justify-center ring-2 ring-white",
            badge.wrapper
          )}
        >
          <ShieldCheck className={cn("text-white", badge.icon)} />
        </div>
      )}
    </div>
  );
};
